import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Gauge, Leaf } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';
import Turnstile from '../components/Turnstile';
import { sessionEndedNotice } from '../lib/sessionEndedNotice';
import '../styles/auth.css';

// Mirrors Turnstile.js's own check: when no site key is configured, that
// component immediately reports an empty token and never renders a widget
// at all (the backend fails OPEN in that case — see app/core/turnstile.py).
// Gating the button on turnstileToken must not apply in that scenario, or
// it would permanently disable the button instead of preserving fail-open.
const TURNSTILE_CONFIGURED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  // Get the redirect path or default to generator. `?redirect=` (not just
  // location.state) matters here because a sub-app on a different path
  // (e.g. MARS at /mars, a separate deployed bundle) can only hand this
  // page a plain URL, not React Router state — that only survives
  // same-app client-side navigations.
  const from = location.state?.from || searchParams.get('redirect') || '/generator';

  // Why this person is here. Everything that signs someone out lands them on
  // this page, and until now the page never said a word about it — so being
  // signed out because a stranger logged into your account was
  // indistinguishable from your session quietly expiring.
  const endedNotice = sessionEndedNotice(searchParams);

  // Maps raw backend errors → friendly user-facing messages (no status codes)
  const friendlyError = (err) => {
    const raw = (err.message || '').toLowerCase();
    const code = err.statusCode;
    // Before the 403 branch: an expired/used Turnstile token is also a 403,
    // and read as "account deactivated" to the student. It is not.
    if (raw.includes('human verification'))
      return '⚠️ The security check on this page expired. Please refresh the page and sign in again.';
    if (code === 401 || raw.includes('incorrect password') || raw.includes('invalid') || raw.includes('credentials'))
      return '⚠️ Incorrect email or password. Please try again.';
    if (code === 404 || raw.includes('not registered') || raw.includes('not found'))
      return '⚠️ No account found with this email. Please sign up first.';
    if (code === 403 || raw.includes('inactive'))
      return '⚠️ Your account has been deactivated. Please contact support.';
    if (code === 429 || raw.includes('too many'))
      return '⚠️ Too many login attempts. Please wait a minute and try again.';
    if (code >= 500 || raw.includes('server'))
      return '⚠️ Something went wrong on our end. Please try again in a moment.';
    if (raw.includes('network') || raw.includes('connection'))
      return '⚠️ Connection error. Please check your internet and try again.';
    return '⚠️ Login failed. Please check your details and try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await login(email, password, turnstileToken);
      // Flag for welcome-back modal on the generator page
      localStorage.setItem('tg_welcome', 'returning');
      // A redirect target pointing at a different sub-app (e.g. /mars) is
      // a separate deployed bundle — react-router's client-side navigate()
      // would just try (and fail) to match it against THIS app's own
      // routes, so it needs a real page load instead.
      if (from.startsWith('http')) {
        // A full absolute URL only ever shows up here in local dev — MARS's
        // own /auth-callback on its separate port (see LoginPage.jsx there),
        // since local dev has no shared origin/localStorage the way
        // production does. Hand the fresh tokens across as query params so
        // that origin can store them itself; AuthCallbackPage.jsx reads and
        // clears them immediately. Never reached in production (there,
        // `from` is always the plain relative '/mars/' below).
        const url = new URL(from);
        url.searchParams.set('token', data.access_token);
        if (data.refresh_token) url.searchParams.set('refresh_token', data.refresh_token);
        window.location.href = url.toString();
      } else if (from.startsWith('/mars')) {
        window.location.href = from;
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      // That attempt consumed the Turnstile token; re-arm for a fresh one so a
      // retry isn't rejected as timeout-or-duplicate.
      setTurnstileToken('');
      setTurnstileReset((n) => n + 1);
      setError(friendlyError(err));
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <Helmet>
        <title>Login | TeachGenie™.ai</title>
        <meta name="description" content="Sign in to your TeachGenie™.ai account. Resume generating world-class lesson plans, exams, and presentations instantly." />
      </Helmet>
      <div className="auth-background">
        <div className="gradient-1"></div>
        <div className="gradient-2"></div>
      </div>

      <div className="auth-wrapper">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <picture>
                <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                <img src="/TechGenieMascot.png" alt="TeachGenie™.ai" className="auth-logo-image" style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }} />
              </picture>
              <h1>Teach<span style={{ color: '#f97316' }}>Genie</span><span style={{ fontSize: '0.6em', color: '#f97316', fontWeight: '900', marginLeft: '2px', verticalAlign: 'super' }}>™</span></h1>
            </div>
            <p className="auth-subtitle">Log in to Continue Your AI Teaching Journey</p>
          </div>

          <div className="auth-tabs">
            <Link to="/login" className="auth-tab active">Login</Link>
            <Link to="/signup" className="auth-tab">Register</Link>
          </div>

          <div className="auth-form-container">
            {/* Not styled as an error: being signed out because the account
                was used elsewhere is information, and in the innocent case
                (you signed in on your other laptop) it is not a fault at all.
                The warning tone is reserved for the reasons that might mean
                somebody else has the password. */}
            {endedNotice && !error && (
              <div
                role="status"
                style={{
                  padding: '12px 16px',
                  marginBottom: '20px',
                  backgroundColor: endedNotice.tone === 'warning' ? '#fef3c7' : '#eff6ff',
                  border: `1px solid ${endedNotice.tone === 'warning' ? '#fcd34d' : '#bfdbfe'}`,
                  borderRadius: '8px',
                  color: endedNotice.tone === 'warning' ? '#92400e' : '#1e40af',
                  fontSize: '14px',
                  lineHeight: 1.5,
                }}
              >
                {endedNotice.text}
              </div>
            )}
            {error && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '20px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '14px',
              }}>
                ⚠️ {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label>
                  <Mail size={20} />
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <Lock size={20} />
                  Password
                </label>
                <div className="password-field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="auth-options">
                <label className="remember-me">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Turnstile onVerify={setTurnstileToken} resetKey={turnstileReset} />
              </div>

              <button
                type="submit"
                className="btn btn-accent btn-large"
                disabled={loading || (TURNSTILE_CONFIGURED && !turnstileToken)}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Logging in...
                  </>
                ) : (TURNSTILE_CONFIGURED && !turnstileToken) ? (
                  'Verifying you\'re human…'
                ) : (
                  'Login to Your Account'
                )}
              </button>

              <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                By logging in, you agree to our{' '}
                <Link to="/policies/terms-and-conditions" className="terms-link">
                  Terms and Conditions
                </Link>
                .
              </p>
            </form>

            <div className="auth-divider">
              <span>or continue with Google</span>
            </div>

            <GoogleSignInButton text="Sign in with Google" className="social-btn w-full justify-center" />

            <div className="auth-footer">
              <p>
                Don't have an account?{' '}
                <Link to="/signup" className="auth-link">Sign up now</Link>
              </p>
            </div>
          </div>
        </div>

        <div className="auth-usp">
          <h2 className="usp-title" style={{ textAlign: 'center' }}>
            <span className="usp-title-navy" style={{ display: 'block', fontSize: '0.85em' }}>Revolutionize Teaching with</span>
            <span className="usp-title-navy">Teach</span>
            <span className="usp-title-gold">Genie</span>
            <span className="usp-title-gold" style={{ fontSize: '0.7em', fontWeight: '900', marginLeft: '2px', verticalAlign: 'super' }}>™</span>
            <span className="usp-title-navy">.ai</span>
          </h2>
          <h3 style={{ color: '#000000', fontSize: '1.25rem', marginTop: '0', marginBottom: '24px', fontWeight: '500', textAlign: 'center' }}>
            Your AI teaching co-instructor
          </h3>

          <div className="usp-energy-grid">
            <div className="usp-energy-card traditional">
              <h4>Traditional Workflow</h4>
              <div className="usp-energy-row">
                <span>1 Lecture</span>
                <strong>approx 12 prompts</strong>
              </div>
              <div className="usp-energy-row">
                <span>1 Prompt</span>
                <strong>0.34 kWh</strong>
              </div>
              <div className="usp-energy-row total">
                <span>Per Course</span>
                <strong>163.2 kWh</strong>
              </div>
            </div>

            <div className="usp-energy-card teachgenie">
              <div className="card-header-with-icon">
                <Leaf size={20} className="sustainability-icon leaf-3d" />
                <h4 className="workflow-title">
                  <span className="teach-text">Teach</span>
                  <span className="genie-text">Genie</span>
                  <span className="tm-text">™</span>
                  <span className="workflow-label">Workflow</span>
                </h4>
              </div>
              <div className="usp-energy-row">
                <span>1 Lecture</span>
                <strong>1 structured generation</strong>
              </div>
              <div className="usp-energy-row">
                <span>Per Lecture</span>
                <strong>0.34 kWh</strong>
              </div>
              <div className="usp-energy-row total">
                <span>Per Course</span>
                <strong>13.6 kWh</strong>
              </div>
            </div>
          </div>

          <div className="usp-impact-strip">
            <div className="impact-chip"><Gauge size={16} /> 91.7% fewer prompts</div>
            <div className="impact-chip"><Leaf size={16} /> 149.6 kWh saved per course</div>
          </div>

          <div className="usp-feature-cloud">
            <span className="usp-feature-pill">No prompt juggling</span>
            <span className="usp-feature-pill">Scenario quizzes</span>
            <span className="usp-feature-pill">Buckle Up points</span>
            <span className="usp-feature-pill">Trends and News</span>
            <span className="usp-feature-pill">Anchor slides ppt</span>
            <span className="usp-feature-pill">student handout pdf</span>
            <span className="usp-feature-pill">RBT level mapping</span>
            <span className="usp-feature-pill">In-class exercises</span>
            <span className="usp-feature-pill">Visual resources</span>
            <span className="usp-feature-pill">All in one framework</span>
          </div>
        </div>
      </div>
    </div>
  );
}
